import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import Mashy from "@/components/Mashy";
import { Users, BookCheck, TrendingUp } from "lucide-react";

export default function Teacher() {
  const [students, setStudents] = useState([]);
  const [summary, setSummary] = useState(null);
  const [active, setActive] = useState(null);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    api.get("/teacher/students").then((r) => setStudents(r.data.students || []));
    api.get("/teacher/class-summary").then((r) => setSummary(r.data));
  }, []);

  const openStudent = async (s) => {
    setActive(s);
    const r = await api.get(`/teacher/student/${s.user_id}/progress`);
    setDetail(r.data);
  };

  return (
    <div className="min-h-screen bg-[#F7F9FC] p-6" data-testid="teacher-screen">
      <header className="max-w-6xl mx-auto flex items-center gap-4 mb-6">
        <Mashy mood="happy" size={70} bounce={false} />
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-gray-500">Teacher Dashboard</div>
          <h1 className="font-display text-3xl">Class Overview</h1>
        </div>
      </header>

      <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <SummaryCard icon={Users} value={summary?.students ?? "—"} label="Students" color="#1CB0F6" />
        <SummaryCard icon={BookCheck} value={summary?.attempts ?? "—"} label="Lesson Attempts" color="#58CC02" />
        <SummaryCard icon={TrendingUp} value={summary ? Math.round((summary.avg_mastery || 0) * 100) + "%" : "—"} label="Avg Mastery" color="#FF9600" />
      </div>

      <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-5">
        <div className="card-chunky p-4 md:col-span-1">
          <div className="font-display text-lg mb-3">Students</div>
          <div className="flex flex-col gap-2 max-h-[60vh] overflow-y-auto">
            {students.length === 0 && <div className="text-sm text-gray-500">No students yet.</div>}
            {students.map((s) => (
              <button key={s.user_id} onClick={() => openStudent(s)} data-testid={`teacher-student-${s.user_id}`}
                className={`text-left p-3 rounded-xl border-2 ${active?.user_id === s.user_id ? "border-[#1CB0F6] bg-[#E8F6FE]" : "border-gray-200 bg-white"}`}>
                <div className="font-bold">{s.full_name}</div>
                <div className="text-xs text-gray-500">{s.email}</div>
                <div className="text-xs mt-1">XP: <b>{s.profile?.total_xp || 0}</b> • Streak: <b>{s.profile?.current_streak || 0}</b></div>
              </button>
            ))}
          </div>
        </div>

        <div className="card-chunky p-5 md:col-span-2">
          {active ? (
            <>
              <div className="font-display text-xl mb-1">{active.full_name}</div>
              <div className="text-sm text-gray-500 mb-4">{active.email}</div>
              <div className="font-display text-base mb-2">Weak Topics</div>
              {detail?.progress?.length ? (
                <ul className="space-y-1 text-sm mb-4">
                  {detail.progress.slice(0, 5).map((p, i) => (
                    <li key={i} className="flex justify-between border-b py-1">
                      <span>{p.topic} <span className="text-gray-400">({p.subject})</span></span>
                      <span className="font-bold text-[#FF9600]">{Math.round((p.mastery_score || 0) * 100)}%</span>
                    </li>
                  ))}
                </ul>
              ) : <div className="text-sm text-gray-500 mb-4">No progress data yet.</div>}
              <div className="font-display text-base mb-2">Recent Attempts</div>
              {detail?.attempts?.length ? (
                <ul className="space-y-1 text-sm">
                  {detail.attempts.slice(0, 6).map((a) => (
                    <li key={a.attempt_id} className="flex justify-between border-b py-1">
                      <span className="truncate">{a.lesson_id}</span>
                      <span>+{a.xp_earned} XP</span>
                    </li>
                  ))}
                </ul>
              ) : <div className="text-sm text-gray-500">No attempts yet.</div>}
            </>
          ) : (
            <div className="text-gray-500 text-sm">Select a student to view their progress.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ icon: Icon, value, label, color }) {
  return (
    <div className="card-chunky p-5 flex items-center gap-4">
      <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: color }}>
        <Icon className="w-6 h-6 text-white" strokeWidth={3} />
      </div>
      <div>
        <div className="font-display text-2xl">{value}</div>
        <div className="text-xs font-bold uppercase tracking-widest text-gray-500">{label}</div>
      </div>
    </div>
  );
}
